import React from 'react';
import { getEnvelopeRecipientsWithTurn } from '../../lib/api/envelope/envelope-api';
import { HandOverDeviceFlow } from './HandOverDeviceFlow';
import { useInPersonSigning } from './use-in-person-signing';
import { candidatesFromRecipients, type IInPersonCandidate } from './utils';
import { WhosSigningModal } from './WhosSigningModal';

interface IProps {
  envelopeId: string;
  /**
   * Decides `isSender`, which is how the sender's own row is kept out of the
   * list. Possibly undefined because useUserInfo() is null until its effects
   * resolve, and the picker lists nobody until then.
   */
  senderEmail: string | undefined;
  onClose: () => void;
}

/**
 * Picks who is signing in person. Recipients are fetched when the picker opens,
 * since the envelope list endpoint does not return them.
 */
export function InPersonSignerPicker({ envelopeId, senderEmail, onClose }: IProps) {
  const [signers, setSigners] = React.useState<IInPersonCandidate[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const signing = useInPersonSigning();

  React.useEffect(() => {
    // senderEmail is null until useUserInfo resolves, so the first pass can fail
    // and the second succeed. Clear the error or it outlives a good list.
    setLoadError(null);
    setSigners([]);

    if (!senderEmail) {
      setLoadError('We could not confirm who you are signed in as. Please refresh and try again.');
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);

    let canceled = false;

    getEnvelopeRecipientsWithTurn(envelopeId)
      .then(({ recipients, signerDetails }) => {
        if (canceled) return;
        setSigners(candidatesFromRecipients(recipients, senderEmail, signerDetails));
      })
      .catch(() => {
        if (!canceled) setLoadError('We could not load the recipients for this envelope.');
      })
      .finally(() => {
        if (!canceled) setIsLoading(false);
      });

    return () => {
      canceled = true;
    };
  }, [envelopeId, senderEmail]);

  function handleSelect(signer: IInPersonCandidate) {
    signing.requestHandOff(envelopeId, signer, 'envelopeList');
  }

  // HandOverDeviceFlow renders these errors; it replaces the list while it is up.
  if (signing.pendingHandOff) return <HandOverDeviceFlow signing={signing} />;

  return (
    <WhosSigningModal
      signers={signers}
      isLoading={isLoading}
      error={loadError || signing.error}
      startingSignerId={signing.startingSignerId}
      onSelect={handleSelect}
      onClose={onClose}
    />
  );
}
